import { packageFonts, type FontFaceData } from './package';
import {
  CoreTypes,
  ErrorCodes,
  ObjectRecordSchema,
  TreeSpecSchema,
  type TreeSpec,
  StyleSchema,
  canvasAccess,
  compareSemver,
  grantable,
  keptGrants,
  meets,
  satisfies,
  typeOwner,
  type CanvasAccessKind,
  type CardInput,
  type CardLook,
  type CardState,
  type CardUse,
  type ComputeInput,
  type LocalizedString,
  CARD_LIMITS,
  undeclaredUses,
  type HostMethodName,
  type HostMethodParams,
  type Id,
  type LinkRecord,
  type ModuleManifest,
  type ObjectPatch,
  type ObjectRecord,
  type ObjectTypeContribution,
  type Permission,
  type Primitive,
  type TextStyle,
} from '@vp/shared-types';
import { approxMeasure, formatDiagnostic, LANG_VERSION, layout as layoutDiagram, parse as parseDiagram } from '@vp/dskhema';
import { commands, DEFAULT_TEXT_STYLE, makeObject, ulid, type CanvasDocument, type Logger, type Metrics, type ModuleRow, type StorageAdapter, type TxContext } from '@vp/core';
import { ModuleHost, type ModuleState } from './host';
import { API_VERSION, apiCompatible, checkPackageRules, PackageError, packageLocales, packageText, parsePackage, type ModulePackage } from './package';
import { RpcError } from './rpc';
import { buildCanvas } from './canvas-build';

/** Render result contract shared with @vp/render (structurally typed to keep packages independent). */
export type RenderResult =
  | { status: 'ready'; prims: Primitive[]; stale?: boolean }
  | { status: 'pending'; prims?: Primitive[] }
  | { status: 'unavailable'; reason?: string };

export interface DeviceAccess {
  openFiles(moduleId: string, opts: { accept?: string[]; multiple?: boolean }): Promise<{ name: string; type: string; size: number; data: Uint8Array }[]>;
  saveFile(moduleId: string, name: string, data: string | Uint8Array, type?: string): Promise<boolean>;
  readClipboard(moduleId: string): Promise<string>;
  writeClipboard(moduleId: string, text: string): Promise<void>;
}

/** API 2.3: what a window of a module needs from the platform's UI. */
export interface WindowSpec {
  handle: string;
  moduleId: string;
  /** Manifest window id, or null for a run-time window. */
  window: string | null;
  title: string;
  html: string;
  width: number;
  height: number;
  resizable: boolean;
  modal: boolean;
  data?: unknown;
}

export interface WindowHost {
  /** A fresh user action is going on (the gesture rule): only then may a module open a window. */
  allowed(): boolean;
  open(spec: WindowSpec): void;
  close(handle: string): void;
  post(handle: string, data: unknown): void;
  setTitle(handle: string, title: string): void;
}

/** API 2.3: open windows of one module at most. */
export const MAX_MODULE_WINDOWS = 4;

/** API 2.6: what new canvases of modules (permission `canvas.create`) need from the platform's UI. */
export interface CanvasHost {
  /** Save a new canvas with its objects (ids, layer keys and canvas id are set). */
  create(meta: { id: Id; name: string }, objects: ObjectRecord[]): Promise<void>;
  /** Show the canvas; false when it may not be done now (no fresh user action). */
  open(id: Id): boolean;
  /** An SVG picture drawn as PNG by the page; null when it cannot be drawn. */
  svgToPng?(data: Uint8Array): Promise<Uint8Array | null>;
}

export interface DocAccess {
  doc(): CanvasDocument | null;
  selection(): Id[];
  notify(level: 'info' | 'warning' | 'error', message: string, objectId?: Id, actions?: { label: string; run: () => void }[]): void;
  /** API 1.6 (UI): name of the open canvas, select objects, show them, draw an arrow between two. */
  canvasName?(): string;
  /** The open canvas is read-only here (another tab has it): module storage of the canvas is read-only too. */
  readOnly?(): boolean;
  setSelection?(ids: Id[]): void;
  focus?(ids: Id[]): void;
  connect?(from: Id, to: Id, opts: { label?: string; arrow?: boolean; module: string }): Id;
  /** API 2.2 (UI): create prepared objects and arrows (ends: index of a new object or an existing id) in one step. */
  insertTree?(objects: PreparedObject[], connectors: { from: number | Id; to: number | Id; label?: string; arrow?: boolean }[], at?: { x: number; y: number }, module?: string, ownOnly?: boolean): Id[];
}

/** An object ready to be created: defaults of its type applied, style checked. */
export interface PreparedObject {
  type: string;
  x: number;
  y: number;
  w: number;
  h: number;
  props: Record<string, unknown>;
  style?: Record<string, unknown>;
  name?: string;
  formatVersion?: number;
}

/** Image type by its first bytes; other files (SVG included) are refused. */
function imageType(d: Uint8Array): string | null {
  if (d[0] === 0x89 && d[1] === 0x50 && d[2] === 0x4e && d[3] === 0x47) return 'image/png';
  if (d[0] === 0xff && d[1] === 0xd8 && d[2] === 0xff) return 'image/jpeg';
  if (d[0] === 0x47 && d[1] === 0x49 && d[2] === 0x46 && d[3] === 0x38) return 'image/gif';
  if (d[0] === 0x52 && d[1] === 0x49 && d[2] === 0x46 && d[3] === 0x46 && d[8] === 0x57 && d[9] === 0x45 && d[10] === 0x42 && d[11] === 0x50) return 'image/webp';
  return null;
}

/** An SVG document by its start (after an optional BOM, XML declaration and comments). */
function looksLikeSvg(d: Uint8Array): boolean {
  const head = new TextDecoder().decode(d.subarray(0, 2048)).replace(/^\uFEFF/, '').trimStart();
  return /^(<\?xml[^>]*>\s*)?(<!--[\s\S]*?-->\s*|<!DOCTYPE[^>]*>\s*)*<svg[\s>]/i.test(head);
}

/** API 2.2: limits of module storage (serialized characters). */
const MODULE_VALUE_LIMIT = 1_000_000;
const MODULE_SCOPE_LIMIT = 5_000_000;

/** Longest text of an object that "text" queries look into. */
function searchableText(o: ObjectRecord): string {
  const p = o.props;
  return [o.name, p['text'], p['label'], p['title']].filter((x): x is string => typeof x === 'string').join(' ').toLowerCase();
}

export interface RuntimeOptions {
  storage: StorageAdapter;
  createWorker: () => Worker;
  locale: () => string;
  logger: Logger;
  metrics?: Metrics;
  docs: DocAccess;
  /** Module list / state changed (UI refresh). */
  onChange?: () => void;
  /** Objects need re-rendering (null = all module objects). */
  onInvalidate?: (ids: Id[] | null) => void;
  onMessages?: (moduleId: string, messages: Record<string, Record<string, string>>) => void;
  onViewPost?: (moduleId: string, objectId: Id, data: unknown) => void;
  /** API 3.0: a module posted a message to one of its profile cards. */
  onProfileCardPost?: (moduleId: string, card: string, data: unknown) => void;
  /** API 4.10: the profile's side of module cards — their state (kept by the platform) and the tray. */
  profileCards?: ProfileCardHost;
  /** API 2.0: a module posted a message to its panel. */
  onPanelPost?: (moduleId: string, data: unknown) => void;
  /** API 2.3: the platform's windows for modules. */
  windows?: WindowHost;
  /** API 2.6: new canvases made by modules. */
  canvases?: CanvasHost;
  /**
   * API 2.0: the platform's file dialogs and clipboard for modules (permissions `files`,
   * `clipboard`). The UI applies the gesture rule: without a fresh user action it asks the user.
   */
  device?: DeviceAccess;
  /** API 2.2: width and height of an image (the browser decodes it); null when it cannot. */
  imageSize?: (blob: Blob) => Promise<{ width: number; height: number } | null>;
  onIsolated?: (moduleId: string, reason: string) => void;
  onCrashed?: (moduleId: string) => void;
  /** Max violations within VIOLATION_WINDOW_MS before isolation (§9.3). */
  violationLimit?: number;
  /** Prior versions kept for offline rollback (§7.7). */
  keepVersions?: number;
  /** Platform fonts for modules, and registration of modules' own fonts on the page. */
  fonts?: {
    /** Platform families with their CSS stacks. */
    platform(): { family: string; css: string }[];
    /** Faces of platform families with embedded data. */
    faces(families: string[]): Promise<FontFaceData[]>;
    /** Make a module's own fonts available to the canvas, views and SVG export. */
    registerModule(moduleId: string, faces: FontFaceData[]): void;
  };
  /** Primitives a module just drew (the app loads the fonts they name). */
  onPrimitives?: (prims: Primitive[]) => void;
  /** Search texts from modules arrived: "Find on canvas" refreshes. */
  onSearchText?: () => void;
  /** Natural size of a DSkhema source (the app measures text with the canvas); an estimate otherwise. */
  diagramSize?: (source: string) => { w: number; h: number };
}

export interface ModuleInfo {
  id: string;
  manifest: ModuleManifest;
  version: string;
  enabled: boolean;
  state: ModuleState;
  granted: Permission[];
  bundled: boolean;
  /** Installed from the catalog with a checked signature (TZ 4): the key that signed it. */
  official: { keyId: string; signedAt: string } | null;
  isolatedReason?: string;
  error?: string;
  previousVersions: string[];
}

/**
 * API 4.10: what the profile keeps for module cards. A card is known to it as `<moduleId>:<card>`.
 */
export interface ProfileCardHost {
  getState(panelId: string): CardState;
  /** Merge values into the card's state. */
  setState(panelId: string, values: CardState): void;
  /** Forget the card's state and place (the card was removed). */
  forget(panelId: string): void;
  /** Put the card on the tray (a card made with `show`). */
  show(panelId: string): void;
}

/** API 4.10: a profile card as the profile shows it. */
export interface ModuleProfileCard {
  card: string;
  title: LocalizedString;
  html: string;
  size?: { w: number; h: number };
  look?: CardLook;
  uses: CardUse[];
  /** Made at run time (`profileCards.create`), not in the manifest. */
  made: boolean;
}

/** A card a module made at run time (stored with the module's data, scope CARDS_SCOPE). */
interface MadeCard {
  title: LocalizedString;
  html: string;
  size?: { w: number; h: number };
  look?: CardLook;
  uses: CardUse[];
}

/** Module data scope of made cards (`card:<id>`) and of the module's looks of its cards (`look:<id>`); modules cannot reach it. */
const CARDS_SCOPE = 'profile-cards';

interface Rec {
  row: ModuleRow;
  manifest: ModuleManifest;
  pkg: ModulePackage;
  host: ModuleHost | null;
  violations: number[];
  crashes: number[];
  error?: string;
  types: Map<string, ObjectTypeContribution>;
  messages: Record<string, Record<string, string>>;
  /** Own fonts from the package. */
  fonts: FontFaceData[];
  /** API 4.10: cards the module made, and its looks of its cards. */
  cards: Map<string, MadeCard>;
  looks: Map<string, CardLook>;
}

interface RenderEntry {
  key: string;
  prims?: Primitive[];
  pendingKey?: string;
  failedKey?: string;
  noRenderer?: boolean;
}

const VIOLATION_WINDOW_MS = 60_000;
/** API 4.6: messages a module's sandboxed documents may send in a second, and the size of one (JSON characters). */
export const SANDBOX_RATE = 200;
export const SANDBOX_MESSAGE_MAX = 1_000_000;
const MAX_CRASH_RESTARTS = 3;
const MAX_INFLIGHT_RENDERS = 16;

export class ModuleError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

/**
 * Module runtime (§7): lifecycle, contributions, rendering of module objects,
 * Platform API implementation and isolation of misbehaving modules.
 */
/** The text style of new objects of a module type with real text (manifest `text.style`), as the editor gives a drawn one. */
function typeTextStyle(info: { contribution: ObjectTypeContribution } | null | undefined): { text?: TextStyle } {
  const t = info?.contribution.text;
  return t ? { text: { ...DEFAULT_TEXT_STYLE, align: 'left', valign: 'top', ...(t.style ?? {}) } } : {};
}

export class ModuleRuntime {
  private mods = new Map<string, Rec>();
  private render = new Map<Id, RenderEntry>();
  private renderKeys = new Map<Id, { rev: number; key: string }>();
  private inflight = new Map<string, number>();
  private queue: (() => void)[] = [];
  private migrating = new Set<string>();
  private failedMigrations = new Set<string>();
  private busy = new Map<Id, number>();
  private zoomBucket = 1;
  private computedStale = new Set<Id>();
  /** API 2.6: canvases each module made since the platform started (only those it may open). */
  private madeCanvases = new Map<string, Set<Id>>();

  constructor(private readonly opts: RuntimeOptions) {}

  // ------------------------------------------------------------ lifecycle

  async init(): Promise<void> {
    const rows = await this.opts.storage.modules.list();
    for (const row of rows) {
      try {
        const pkg = parsePackage(new Uint8Array(await row.archive.arrayBuffer()));
        const rec = this.makeRec(row, pkg);
        this.mods.set(row.id, rec);
        await this.loadCards(rec);
      } catch (e) {
        this.opts.logger.error('module.load', { moduleId: row.id, error: e instanceof Error ? e.name : 'unknown' });
      }
    }
    await Promise.all([...this.mods.values()].map((r) => this.startIfEnabled(r)));
    this.changed();
  }

  private makeRec(row: ModuleRow, pkg: ModulePackage): Rec {
    const types = new Map<string, ObjectTypeContribution>();
    for (const t of pkg.manifest.contributes.objectTypes ?? []) types.set(`${pkg.manifest.id}:${t.type}`, t);
    const messages = packageLocales(pkg);
    this.opts.onMessages?.(pkg.manifest.id, messages);
    return { row, manifest: pkg.manifest, pkg, host: null, violations: [], crashes: [], types, messages, fonts: packageFonts(pkg), cards: new Map(), looks: new Map() };
  }

  private changed(): void {
    this.opts.onChange?.();
  }

  private async startIfEnabled(rec: Rec): Promise<void> {
    if (!rec.row.enabled || rec.row.isolated) {
      this.closeWindowsOf(rec.manifest.id);
      rec.host?.stop(rec.row.isolated ? 'isolated' : 'disabled');
      return;
    }
    if (!apiCompatible(rec.manifest)) {
      rec.error = `requires API ${rec.manifest.apiVersion}, platform ${API_VERSION}`;
      rec.host = null;
      return;
    }
    const code = packageText(rec.pkg, rec.manifest.entry);
    if (code === null) return;
    const host = new ModuleHost(rec.manifest, rec.row.granted as Permission[], {
      createWorker: this.opts.createWorker,
      handle: (id, method, params) => this.handle(id, method, params),
      onEvent: (id, method, params) => this.onModuleEvent(id, method, params),
      onViolation: (id, reason, serious) => this.violation(id, reason, serious),
      onCrash: (id, err) => void this.onCrash(id, err),
      onTiming: (_id, _m, ms) => this.opts.metrics?.record('module.api', ms),
    });
    rec.host = host;
    rec.error = undefined;
    if (rec.fonts.length) this.opts.fonts?.registerModule(rec.manifest.id, rec.fonts);
    // a (re)started module subscribes again from its activate
    this.changeListeners.delete(rec.manifest.id);
    try {
      await host.start(code, rec.messages, this.opts.locale(), API_VERSION);
      this.opts.logger.info('module.start', { moduleId: rec.manifest.id, version: rec.manifest.version });
      // it learns the canvas that is open now
      const doc = this.opts.docs.doc();
      if (this.currentCanvas && doc && meets(host.granted, 'canvas.read')) host.emit('canvas.opened', { id: this.currentCanvas, name: this.opts.docs.canvasName?.() ?? '' });
    } catch (e) {
      rec.error = e instanceof Error ? e.message : String(e);
      this.opts.logger.error('module.start.failed', { moduleId: rec.manifest.id, code: (e as { code?: string }).code ?? 'unknown' });
    }
    this.invalidateModule(rec.manifest.id);
  }

  /** Install or update from a package archive (§7.2, §7.7). */
  /**
   * `official` — the package's catalog signature was checked by the caller (the app checks it
   * against the keys built into it): such a module installs without developer mode.
   */
  async install(data: Uint8Array, opts: { devMode: boolean; bundled?: boolean; granted?: Permission[]; official?: { keyId: string; signedAt: string } }): Promise<{ manifest: ModuleManifest; updated: boolean; previous?: string }> {
    const pkg = parsePackage(data);
    const m = pkg.manifest;
    if (!opts.bundled && !opts.official && !opts.devMode) throw new ModuleError('E_DEV_MODE', 'developer mode is required to install third-party modules');
    if (!apiCompatible(m)) throw new ModuleError('E_API', `module requires API ${m.apiVersion}, platform provides ${API_VERSION}`);
    // API 2.2: the modules it needs must be there, in a fitting version
    for (const [dep, range] of Object.entries(m.dependencies ?? {})) {
      if (dep === m.id) throw new ModuleError('E_MANIFEST', 'a module cannot depend on itself');
      const have = this.mods.get(dep);
      if (!have) throw new ModuleError('E_DEPENDENCY', `needs module ${dep} ${range}`);
      if (!satisfies(have.manifest.version, range)) throw new ModuleError('E_DEPENDENCY', `needs module ${dep} ${range}, installed ${have.manifest.version}`);
    }
    // a module may bring fonts but not replace the platform's (text everywhere would change)
    const platformFonts = new Set((this.opts.fonts?.platform() ?? []).map((f) => f.family.toLowerCase()));
    const clash = (m.contributes.fonts ?? []).find((f) => platformFonts.has(f.family.toLowerCase()));
    if (clash) throw new ModuleError('E_FONT', `font family "${clash.family}" is a platform font`);
    // the package's own rules — the catalog server checks the same (checkPackageRules)
    checkPackageRules(pkg);
    // a shortcut belongs to one module
    for (const s of m.contributes.shortcuts ?? []) {
      for (const r of this.mods.values()) {
        if (r.manifest.id !== m.id && (r.manifest.contributes.shortcuts ?? []).some((x) => x.keys === s.keys)) throw new ModuleError('E_MANIFEST', `shortcut ${s.keys} is already taken by module ${r.manifest.id}`);
      }
    }
    const existing = this.mods.get(m.id);
    const archive = new Blob([data as BlobPart], { type: 'application/zip' });
    let previous: string | undefined;
    if (existing) {
      previous = existing.manifest.version;
      // keep the previous version for offline rollback
      await this.opts.storage.modules.putVersion({ key: `${m.id}@${previous}`, id: m.id, version: previous, archive: existing.row.archive, manifest: existing.manifest, savedAt: Date.now() });
      await this.pruneVersions(m.id, m.version);
      if (existing) this.closeWindowsOf(existing.manifest.id);
      existing.host?.stop();
    }
    const requested = m.permissions;
    const allowed = grantable(requested);
    const granted = opts.granted ? opts.granted.filter((p) => allowed.has(p)) : existing ? keptGrants(requested, existing.row.granted as Permission[]) : opts.bundled ? requested : [];
    const row: ModuleRow = {
      id: m.id,
      version: m.version,
      enabled: true,
      installedAt: Date.now(),
      archive,
      manifest: m,
      granted,
      bundled: !!opts.bundled,
      official: opts.official ?? null,
      isolated: null,
    };
    await this.opts.storage.modules.put(row);
    const rec = this.makeRec(row, pkg);
    this.mods.set(m.id, rec);
    await this.loadCards(rec);
    this.clearModuleCaches(m.id);
    await this.startIfEnabled(rec);
    this.opts.logger.info(existing ? 'module.update' : 'module.install', { moduleId: m.id, version: m.version, previous: previous ?? '' });
    this.changed();
    return { manifest: m, updated: !!existing, previous };
  }

  private async pruneVersions(id: string, current: string): Promise<void> {
    const keep = this.opts.keepVersions ?? 2;
    const versions = (await this.opts.storage.modules.versions(id)).filter((v) => v.version !== current);
    for (const v of versions.slice(keep)) await this.opts.storage.modules.deleteVersion(v.key);
  }

  async previousVersions(id: string): Promise<string[]> {
    const cur = this.mods.get(id)?.manifest.version;
    return (await this.opts.storage.modules.versions(id)).map((v) => v.version).filter((v) => v !== cur);
  }

  /** Roll back to a stored previous version, offline (§7.7). */
  async rollback(id: string, version: string): Promise<void> {
    const rec = this.mods.get(id);
    const versions = await this.opts.storage.modules.versions(id);
    const target = versions.find((v) => v.version === version);
    if (!rec || !target) throw new ModuleError(ErrorCodes.notFound, `version ${version} not found`);
    const data = new Uint8Array(await target.archive.arrayBuffer());
    const pkg = parsePackage(data);
    await this.opts.storage.modules.putVersion({ key: `${id}@${rec.manifest.version}`, id, version: rec.manifest.version, archive: rec.row.archive, manifest: rec.manifest, savedAt: Date.now() });
    await this.opts.storage.modules.deleteVersion(target.key);
    if (rec) this.closeWindowsOf(rec.manifest.id);
    rec.host?.stop();
    const row: ModuleRow = { ...rec.row, version: pkg.manifest.version, archive: target.archive, manifest: pkg.manifest, granted: keptGrants(pkg.manifest.permissions, rec.row.granted as Permission[]) };
    await this.opts.storage.modules.put(row);
    const next = this.makeRec(row, pkg);
    this.mods.set(id, next);
    await this.loadCards(next);
    this.clearModuleCaches(id);
    await this.startIfEnabled(next);
    this.opts.logger.info('module.rollback', { moduleId: id, version });
    this.changed();
  }

  async setEnabled(id: string, enabled: boolean): Promise<void> {
    const rec = this.mods.get(id);
    if (!rec) return;
    rec.row = { ...rec.row, enabled, isolated: enabled ? null : rec.row.isolated };
    if (enabled) rec.violations = [];
    await this.opts.storage.modules.put(rec.row);
    if (enabled) await this.startIfEnabled(rec);
    else {
      if (rec) this.closeWindowsOf(rec.manifest.id);
      rec.host?.stop('disabled');
      this.invalidateModule(id);
    }
    this.opts.logger.info(enabled ? 'module.enable' : 'module.disable', { moduleId: id });
    this.changed();
  }

  /** Clear isolation / crash state and start again. */
  async restart(id: string): Promise<void> {
    const rec = this.mods.get(id);
    if (!rec) return;
    rec.crashes = [];
    rec.violations = [];
    rec.row = { ...rec.row, isolated: null, enabled: true };
    await this.opts.storage.modules.put(rec.row);
    await this.startIfEnabled(rec);
    this.changed();
  }

  /** Uninstall; objects are kept and become unavailable (§7.6). */
  async uninstall(id: string): Promise<void> {
    const rec = this.mods.get(id);
    if (rec) this.closeWindowsOf(rec.manifest.id);
    rec?.host?.stop();
    this.mods.delete(id);
    const dependents = this.dependentsOf(id);
    if (dependents.length) throw new ModuleError('E_DEPENDENCY', `module ${dependents.join(', ')} needs it`);
    await this.opts.storage.modules.delete(id);
    // its own data goes; data it kept for canvases stays with them (it comes back if the module does)
    await this.opts.storage.moduleData.deleteScope(id, 'app');
    // API 4.10: its cards go with it
    for (const c of rec?.manifest.contributes.profileCards ?? []) this.opts.profileCards?.forget(`${id}:${c.id}`);
    for (const c of rec?.cards.keys() ?? []) this.opts.profileCards?.forget(`${id}:${c}`);
    await this.opts.storage.moduleData.deleteScope(id, CARDS_SCOPE);
    this.clearModuleCaches(id);
    this.opts.logger.info('module.uninstall', { moduleId: id });
    this.changed();
  }

  /** API 1.6: values of a module's settings — the manifest defaults with the user's values over them. */
  settingsOf(id: string): Record<string, unknown> {
    const rec = this.mods.get(id);
    if (!rec) return {};
    const out: Record<string, unknown> = {};
    for (const f of rec.manifest.contributes.settings ?? []) if ('default' in f && f.default !== undefined) out[f.key] = f.default;
    return { ...out, ...(rec.row.settings ?? {}) };
  }

  /** API 1.6: the user changed a module's settings (only declared keys are kept); the module is told. */
  async setSettings(id: string, values: Record<string, unknown>): Promise<void> {
    const rec = this.mods.get(id);
    if (!rec) return;
    const keys = new Set((rec.manifest.contributes.settings ?? []).map((f) => f.key));
    const clean: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(values)) if (keys.has(k) && (v === null || ['string', 'number', 'boolean'].includes(typeof v))) clean[k] = v;
    rec.row = { ...rec.row, settings: { ...(rec.row.settings ?? {}), ...clean } };
    await this.opts.storage.modules.put(rec.row);
    rec.host?.emit('settings.changed', { values: this.settingsOf(id) });
    this.opts.logger.info('module.settings', { moduleId: id, keys: Object.keys(clean) });
    this.changed();
  }

  /**
   * API 2.2: objects of a tree spec ready to be created (types the module may create, defaults of its
   * types, checked styles) and arrows with resolved ends (index of a new object or an existing id).
   */
  private prepareTree(moduleId: string, spec: TreeSpec, existingIds: boolean): { objects: PreparedObject[]; connectors: { from: number | Id; to: number | Id; label?: string; arrow?: boolean }[]; refs: Map<string, number> } {
    const refs = new Map<string, number>();
    const objects = spec.objects.map((o, i) => {
      const owner = typeOwner(o.type);
      const info = owner ? this.typeInfo(o.type) : null;
      if (owner && (!info || owner !== moduleId)) throw new RpcError(ErrorCodes.forbidden, `type ${o.type} cannot be created by ${moduleId}`);
      if (o.ref) {
        if (refs.has(o.ref)) throw new RpcError(ErrorCodes.invalidParams, `ref ${o.ref} is used twice`);
        refs.set(o.ref, i);
      }
      const style = { ...(info?.contribution.defaultStyle ?? {}), ...(o.style ? validStyle(o.style) : {}) };
      return {
        type: o.type,
        x: o.x,
        y: o.y,
        w: o.w,
        h: o.h,
        props: { ...(info?.contribution.defaultProps ?? {}), ...(o.props ?? {}) },
        ...(Object.keys(style).length ? { style } : {}),
        ...(o.name !== undefined ? { name: o.name } : {}),
        ...(info ? { formatVersion: info.contribution.formatVersion } : {}),
      };
    });
    const doc = this.opts.docs.doc();
    const end = (e: string): number | Id => {
      const i = refs.get(e);
      if (i !== undefined) return i;
      if (existingIds && doc?.get(e) && this.reachable(moduleId, e)) return e;
      throw new RpcError(ErrorCodes.notFound, `arrow end ${e} is neither a ref nor an object`);
    };
    const connectors = (spec.connectors ?? []).map((c) => ({ from: end(c.from), to: end(c.to), label: c.label, arrow: c.arrow }));
    return { objects, connectors, refs };
  }

  /** API 2.2: installed modules that need this one (manifest `dependencies`). */
  dependentsOf(id: string): string[] {
    return [...this.mods.values()].filter((r) => r.manifest.dependencies && id in r.manifest.dependencies).map((r) => r.manifest.id);
  }

  /** API 2.2: templates of running modules (manifest `contributes.templates`). */
  templates(): { moduleId: string; id: string; name: string | Record<string, string>; description?: string | Record<string, string> }[] {
    const out: { moduleId: string; id: string; name: string | Record<string, string>; description?: string | Record<string, string> }[] = [];
    for (const r of this.mods.values()) {
      if (r.host?.state !== 'running') continue;
      for (const t of r.manifest.contributes.templates ?? []) out.push({ moduleId: r.manifest.id, id: t.id, name: t.name, description: t.description });
    }
    return out;
  }

  /** API 2.2: insert a module's template centred on a point, in one step; ids of the new objects. */
  insertTemplate(moduleId: string, id: string, center: { x: number; y: number }): Id[] {
    const rec = this.mods.get(moduleId);
    const t = rec?.manifest.contributes.templates?.find((x) => x.id === id);
    if (!rec || !t || !this.opts.docs.insertTree) return [];
    const spec = TreeSpecSchema.parse(JSON.parse(packageText(rec.pkg, t.file) ?? 'null'));
    // "$t:key" strings (props, names, arrow labels) come from the module's locales in the UI language
    const lang = this.opts.locale();
    const tr = (v: unknown): unknown => {
      const m = typeof v === 'string' ? /^\$t:([\w.-]+)$/.exec(v) : null;
      return m ? (rec.messages[lang]?.[m[1]] ?? rec.messages['en']?.[m[1]] ?? m[1]) : v;
    };
    for (const o of spec.objects) {
      if (o.props) o.props = Object.fromEntries(Object.entries(o.props).map(([k, v]) => [k, tr(v)]));
      if (o.name !== undefined) o.name = tr(o.name) as string;
    }
    for (const c of spec.connectors ?? []) if (c.label !== undefined) c.label = tr(c.label) as string;
    const { objects, connectors } = this.prepareTree(moduleId, spec, false);
    const x0 = Math.min(...objects.map((o) => o.x));
    const y0 = Math.min(...objects.map((o) => o.y));
    const w = Math.max(...objects.map((o) => o.x + o.w)) - x0;
    const h = Math.max(...objects.map((o) => o.y + o.h)) - y0;
    this.opts.logger.info('module.template', { moduleId, id, objects: objects.length });
    return this.opts.docs.insertTree(objects, connectors, { x: center.x - w / 2, y: center.y - h / 2 });
  }

  /** API 2.2: the module's own storage; `canvas` — of the open canvas, `app` — across canvases. */
  private async storageCall(moduleId: string, method: string, p: { scope: 'canvas' | 'app'; key?: string; value?: unknown; prefix?: string }): Promise<unknown> {
    let scope = 'app';
    if (p.scope === 'canvas') {
      const doc = this.opts.docs.doc();
      if (!doc) throw new RpcError(ErrorCodes.notFound, 'no canvas is open');
      scope = doc.canvasId;
    }
    const store = this.opts.storage.moduleData;
    if (method === 'storage.get') return { value: (await store.get(moduleId, scope, p.key!))?.value ?? null };
    if (method === 'storage.list') {
      const keys = (await store.list(moduleId, scope)).map((r) => r.key).filter((k) => !p.prefix || k.startsWith(p.prefix));
      return { keys: keys.sort() };
    }
    if (p.scope === 'canvas' && this.opts.docs.readOnly?.()) throw new RpcError('E_READ_ONLY', 'the canvas is read-only here');
    if (method === 'storage.delete') {
      await store.delete(moduleId, scope, p.key!);
      return {};
    }
    let json: string;
    try {
      json = JSON.stringify(p.value ?? null);
    } catch {
      throw new RpcError(ErrorCodes.invalidParams, 'value is not JSON');
    }
    if (json.length > MODULE_VALUE_LIMIT) throw new RpcError(ErrorCodes.invalidParams, 'value is larger than 1 MB');
    const rows = await store.list(moduleId, scope);
    const used = rows.reduce((s, r) => s + (r.key === p.key ? 0 : r.size), 0);
    if (used + json.length > MODULE_SCOPE_LIMIT) throw new RpcError('E_QUOTA', 'module storage is full (5 MB)');
    await store.put({ moduleId, scope, key: p.key!, value: JSON.parse(json), size: json.length, updatedAt: Date.now() });
    return {};
  }

  /** API 1.6: tools modules give to AI gateways, of running modules. */
  mcpTools(exceptModule?: string): { moduleId: string; name: string; description: string; inputSchema?: Record<string, unknown> }[] {
    const out: { moduleId: string; name: string; description: string; inputSchema?: Record<string, unknown> }[] = [];
    for (const r of this.mods.values()) {
      if (r.manifest.id === exceptModule || r.host?.state !== 'running') continue;
      for (const t of r.manifest.contributes.mcpTools ?? []) out.push({ moduleId: r.manifest.id, name: t.name, description: t.description, inputSchema: t.inputSchema });
    }
    return out;
  }

  async setGranted(id: string, granted: Permission[]): Promise<void> {
    const rec = this.mods.get(id);
    if (!rec) return;
    const can = grantable(rec.manifest.permissions);
    const allowed = granted.filter((p) => can.has(p));
    rec.row = { ...rec.row, granted: allowed };
    await this.opts.storage.modules.put(rec.row);
    rec.host?.setGranted(allowed);
    this.opts.logger.info('module.permissions', { moduleId: id, granted: allowed });
    this.changed();
  }

  stopAll(): void {
    for (const r of this.mods.values()) {
      this.closeWindowsOf(r.manifest.id);
      r.host?.stop();
    }
  }

  // ------------------------------------------------------------ info

  list(): ModuleInfo[] {
    return [...this.mods.values()].map((r) => ({
      id: r.manifest.id,
      manifest: r.manifest,
      version: r.manifest.version,
      enabled: r.row.enabled,
      state: r.row.isolated ? 'isolated' : !apiCompatible(r.manifest) ? 'incompatible' : !r.row.enabled ? 'disabled' : r.host?.state ?? 'stopped',
      granted: r.row.granted as Permission[],
      bundled: !!r.row.bundled,
      official: r.row.official ?? null,
      isolatedReason: r.row.isolated?.reason,
      error: r.error,
      previousVersions: [],
    }));
  }

  get(id: string): ModuleInfo | undefined {
    return this.list().find((m) => m.id === id);
  }

  /** Package icon as a data URL (rendered via <img>, never injected as markup). */
  iconDataUrl(id: string): string | null {
    const rec = this.mods.get(id);
    const path = rec?.manifest.icon;
    const data = path ? rec?.pkg.files.get(path) : undefined;
    if (!data) return null;
    const mime = path!.endsWith('.svg') ? 'image/svg+xml' : path!.endsWith('.png') ? 'image/png' : null;
    if (!mime) return null;
    let bin = '';
    for (let i = 0; i < data.length; i += 0x8000) bin += String.fromCharCode(...data.subarray(i, i + 0x8000));
    return `data:${mime};base64,${btoa(bin)}`;
  }

  messages(id: string): Record<string, Record<string, string>> | undefined {
    return this.mods.get(id)?.messages;
  }

  isModuleAvailable(id: string): boolean {
    return this.mods.get(id)?.host?.state === 'running';
  }

  typeFormatVersion(type: string): number | undefined {
    const owner = typeOwner(type);
    return owner ? this.mods.get(owner)?.types.get(type)?.formatVersion : undefined;
  }

  typeInfo(type: string): { moduleId: string; manifest: ModuleManifest; contribution: ObjectTypeContribution } | null {
    const owner = typeOwner(type);
    const rec = owner ? this.mods.get(owner) : undefined;
    const c = rec?.types.get(type);
    return rec && c ? { moduleId: rec.manifest.id, manifest: rec.manifest, contribution: c } : null;
  }

  /** Object types, tools, link types and commands of running modules. */
  contributions(): { moduleId: string; manifest: ModuleManifest }[] {
    return [...this.mods.values()].filter((r) => r.host?.state === 'running').map((r) => ({ moduleId: r.manifest.id, manifest: r.manifest }));
  }

  /** DSkhema node forms of running modules: alias (lower case) → object type; first module wins. */
  diagramShapes(): Map<string, string> {
    const out = new Map<string, string>();
    for (const { moduleId, manifest } of this.contributions()) {
      const own = new Set((manifest.contributes.objectTypes ?? []).map((t) => t.type));
      for (const ds of manifest.contributes.diagramShapes ?? []) {
        if (!own.has(ds.objectType)) continue;
        for (const a of ds.aliases) {
          const key = a.toLowerCase();
          if (!out.has(key)) out.set(key, `${moduleId}:${ds.objectType}`);
        }
      }
    }
    return out;
  }

  private diagramSize(source: string): { w: number; h: number } {
    if (this.opts.diagramSize) return this.opts.diagramSize(source);
    const { model } = parseDiagram(source, { shapes: this.diagramShapes() });
    if (!model) return { w: 240, h: 120 };
    const g = layoutDiagram(model, approxMeasure);
    return { w: g.width, h: g.height };
  }

  linkType(type: string): { moduleId: string; directed: boolean; from?: string[]; to?: string[] } | null {
    const owner = typeOwner(type);
    const rec = owner ? this.mods.get(owner) : undefined;
    if (!rec || rec.host?.state !== 'running') return null;
    const local = type.slice(type.lastIndexOf(':') + 1);
    const lt = rec.manifest.contributes.linkTypes?.find((l) => l.type === local);
    return lt ? { moduleId: rec.manifest.id, directed: lt.directed, from: lt.from, to: lt.to } : null;
  }

  isUnavailable(o: ObjectRecord): boolean {
    if (o.type.startsWith('core:')) return false;
    const info = this.typeInfo(o.type);
    return !info || !this.isModuleAvailable(info.moduleId) || o.formatVersion > info.contribution.formatVersion;
  }

  /** Count objects that a new manifest cannot handle (type removed or format downgraded) (§7.7). */
  incompatibleObjects(newManifest: ModuleManifest, objects: Iterable<ObjectRecord>): number {
    const types = new Map((newManifest.contributes.objectTypes ?? []).map((t) => [`${newManifest.id}:${t.type}`, t.formatVersion]));
    let n = 0;
    for (const o of objects) {
      if (typeOwner(o.type) !== newManifest.id) continue;
      const fv = types.get(o.type);
      if (fv === undefined || o.formatVersion > fv) n++;
    }
    return n;
  }

  stats(): { running: number; total: number; pending: number } {
    let running = 0;
    let pending = 0;
    for (const r of this.mods.values()) {
      if (r.host?.state === 'running') running++;
      pending += r.host?.pending ?? 0;
    }
    return { running, total: this.mods.size, pending };
  }

  // ------------------------------------------------------------ violations & crashes

  private violation(id: string, reason: string, serious: boolean): void {
    const rec = this.mods.get(id);
    if (!rec) return;
    const now = Date.now();
    rec.violations = rec.violations.filter((t) => now - t < VIOLATION_WINDOW_MS);
    rec.violations.push(now);
    this.opts.logger.warn('module.violation', { moduleId: id, reason: reason.replace(/\s+/g, '_').slice(0, 100), count: rec.violations.length });
    if (serious || rec.violations.length >= (this.opts.violationLimit ?? 5)) void this.isolate(id, reason);
  }

  /** Isolate a module: stop it; its objects become unavailable, data is kept (§9.3). */
  async isolate(id: string, reason: string): Promise<void> {
    const rec = this.mods.get(id);
    if (!rec || rec.row.isolated) return;
    if (rec) this.closeWindowsOf(rec.manifest.id);
    rec.host?.stop('isolated');
    rec.row = { ...rec.row, isolated: { at: Date.now(), reason } };
    await this.opts.storage.modules.put(rec.row).catch(() => undefined);
    this.opts.logger.error('module.isolated', { moduleId: id });
    this.invalidateModule(id);
    this.opts.onIsolated?.(id, reason);
    this.changed();
  }

  private async onCrash(id: string, error: string): Promise<void> {
    const rec = this.mods.get(id);
    if (!rec) return;
    const now = Date.now();
    rec.crashes = rec.crashes.filter((t) => now - t < VIOLATION_WINDOW_MS);
    rec.crashes.push(now);
    this.opts.logger.error('module.crash', { moduleId: id, count: rec.crashes.length });
    void error;
    this.invalidateModule(id);
    this.changed();
    if (rec.crashes.length > MAX_CRASH_RESTARTS) {
      await this.isolate(id, 'repeated crashes');
      return;
    }
    this.opts.onCrashed?.(id);
    await new Promise((r) => setTimeout(r, 300 * rec.crashes.length));
    if (this.mods.get(id) === rec && !rec.row.isolated && rec.row.enabled) await this.startIfEnabled(rec);
    this.changed();
  }

  // ------------------------------------------------------------ events

  private onModuleEvent(id: string, method: string, params: unknown): void {
    if (method === 'invalidate') {
      const ids = (params as { ids: Id[] }).ids.filter((oid) => {
        const o = this.opts.docs.doc()?.get(oid);
        return o && typeOwner(o.type) === id;
      });
      for (const oid of ids) this.render.delete(oid);
      if (ids.length) this.opts.onInvalidate?.(ids);
    } else if (method === 'subscribe') {
      if ((params as { event: string }).event === 'canvas.changed') this.changeListeners.add(id);
    } else if (method === 'window.post') {
      const p = params as { handle: string; data: unknown };
      if (this.windows.get(p.handle)?.moduleId === id) this.opts.windows?.post(p.handle, p.data);
    } else if (method === 'profileCard.post') {
      const p = params as { card: string; data: unknown };
      if (this.mods.get(id)?.manifest.contributes.profileCards?.some((c) => c.id === p.card)) this.opts.onProfileCardPost?.(id, p.card, p.data);
    } else if (method === 'panel.post') {
      if (this.mods.get(id)?.manifest.contributes.panel) this.opts.onPanelPost?.(id, (params as { data: unknown }).data);
    } else if (method === 'view.post') {
      const p = params as { objectId: Id; data: unknown };
      // a module talks only to the views of its own objects
      const o = this.opts.docs.doc()?.get(p.objectId);
      if (o && typeOwner(o.type) === id) this.opts.onViewPost?.(id, p.objectId, p.data);
    }
  }

  /** Broadcast a platform event to all running modules. */
  broadcast(event: string, params: unknown): void {
    for (const r of this.mods.values()) r.host?.emit(event, params);
  }

  /** The user's selection changed: to modules that may read, each with the objects it may read (API 4.0). */
  notifySelection(ids: Id[]): void {
    for (const r of this.mods.values()) {
      if (!r.host || !meets(r.host.granted, 'canvas.read')) continue;
      r.host.emit('selection.changed', { ids: this.readable(r.manifest.id, ids) });
    }
  }

  /** Notify modules about changed objects of their own types. */
  notifyChanged(ids: Id[]): void {
    const doc = this.opts.docs.doc();
    if (!doc || !ids.length) return;
    const byModule = new Map<string, Id[]>();
    for (const id of ids) {
      const o = doc.get(id);
      const owner = o ? typeOwner(o.type) : null;
      if (!owner) continue;
      let list = byModule.get(owner);
      if (!list) byModule.set(owner, (list = []));
      list.push(id);
    }
    for (const [m, list] of byModule) this.mods.get(m)?.host?.emit('objects.changed', { ids: list });
    // API 2.2: modules that listen to all changes (and may read the canvas)
    for (const m of this.changeListeners) {
      const host = this.mods.get(m)?.host;
      if (!host || !meets(host.granted, 'canvas.read')) continue;
      const seen = ids.filter((id) => {
        const o = doc.get(id);
        return !!o && this.may(m, 'read', o);
      });
      if (seen.length) host.emit('canvas.changed', { ids: seen });
    }
  }

  /** API 2.2: modules listening to `canvas.changed`. */
  private changeListeners = new Set<string>();
  private currentCanvas: string | null = null;

  /** API 2.2: the current canvas changed (null — none): `canvas.closed` / `canvas.opened` to modules that may read it. */
  setCanvas(id: string | null, name: string): void {
    if (id === this.currentCanvas) return;
    const prev = this.currentCanvas;
    this.currentCanvas = id;
    for (const r of this.mods.values()) {
      if (!r.host || !meets(r.host.granted, 'canvas.read')) continue;
      if (prev) r.host.emit('canvas.closed', { id: prev });
      if (id) r.host.emit('canvas.opened', { id, name });
    }
  }

  private searchTexts = new Map<Id, { key: string; text?: string | null; pending?: boolean }>();

  /**
   * The text a module gives for an object (registerSearchText): the string, null when the module
   * gives none (search its drawing instead), or undefined while it is being asked for.
   */
  searchText(obj: ObjectRecord, rev: number): string | null | undefined {
    const owner = typeOwner(obj.type);
    const rec = owner ? this.mods.get(owner) : undefined;
    if (!rec?.host || rec.host.state !== 'running') return null;
    const key = rev + ':' + rec.manifest.version;
    let e = this.searchTexts.get(obj.id);
    if (e && e.key === key && !e.pending) return e.text ?? null;
    if (e && e.key === key && e.pending) return undefined;
    const prev = e?.text;
    e = { key, pending: true, text: prev };
    this.searchTexts.set(obj.id, e);
    const entry = e;
    const host = rec.host;
    this.schedule(rec.manifest.id, async () => {
      try {
        const res = (await host.call('searchText', { type: obj.type, object: obj })) as { text: string | null };
        entry.text = typeof res.text === 'string' ? res.text : null;
      } catch {
        entry.text = null;
      }
      entry.pending = false;
      this.opts.onSearchText?.();
    });
    return prev === undefined ? undefined : prev;
  }

  // ------------------------------------------------------------ windows (API 2.3)

  private windows = new Map<string, { moduleId: string; window: string | null }>();

  private openWindow(moduleId: string, p: HostMethodParams<'windows.open'>): { handle: string } {
    const host = this.opts.windows;
    const rec = this.mods.get(moduleId);
    if (!host || !rec) throw new RpcError(ErrorCodes.unknownMethod, 'windows are not available here');
    if (!host.allowed()) throw new RpcError(ErrorCodes.forbidden, 'a window opens only in answer to a user action');
    if ([...this.windows.values()].filter((w) => w.moduleId === moduleId).length >= MAX_MODULE_WINDOWS) throw new RpcError(ErrorCodes.forbidden, `no more than ${MAX_MODULE_WINDOWS} windows at a time`);
    let html: string | null = null;
    let title = p.title ?? '';
    let width = p.width;
    let height = p.height;
    let resizable = p.resizable;
    let modal = p.modal;
    if (p.window) {
      const w = rec.manifest.contributes.windows?.find((x) => x.id === p.window);
      if (!w) throw new RpcError(ErrorCodes.notFound, `unknown window ${p.window}`);
      html = packageText(rec.pkg, w.file);
      title = p.title ?? (typeof w.title === 'string' ? w.title : (w.title[this.opts.locale()] ?? w.title['en'] ?? Object.values(w.title)[0] ?? ''));
      width ??= w.width;
      height ??= w.height;
      resizable ??= w.resizable;
      modal ??= w.modal;
    } else html = p.html ?? null;
    if (html === null) throw new RpcError(ErrorCodes.invalidParams, 'window or html is required');
    const handle = 'w' + ulid();
    this.windows.set(handle, { moduleId, window: p.window ?? null });
    host.open({ handle, moduleId, window: p.window ?? null, title: title.slice(0, 120), html, width: width ?? 480, height: height ?? 360, resizable: resizable ?? true, modal: modal ?? false, data: p.data });
    return { handle };
  }

  private ownWindow(moduleId: string, handle: string): void {
    if (this.windows.get(handle)?.moduleId !== moduleId) throw new RpcError(ErrorCodes.notFound, 'no such window');
  }

  /** A window sent a message to its module. */
  windowMessage(handle: string, data: unknown): void {
    const w = this.windows.get(handle);
    if (w) this.mods.get(w.moduleId)?.host?.emit('window.message', { handle, window: w.window, data });
  }

  /** A window is gone (closed by the user or the platform): its module hears of it. */
  windowClosed(handle: string): void {
    const w = this.windows.get(handle);
    if (!w) return;
    this.windows.delete(handle);
    this.mods.get(w.moduleId)?.host?.emit('window.closed', { handle, window: w.window });
  }

  /** Close every window of a module (it stopped, was disabled or removed). */
  closeWindowsOf(moduleId: string): void {
    for (const [handle, w] of [...this.windows]) {
      if (w.moduleId !== moduleId) continue;
      this.windows.delete(handle);
      this.opts.windows?.close(handle);
    }
  }

  /** API 2.0: HTML of the module's panel (manifest `contributes.panel.file`), or null. */
  panelHtml(moduleId: string): string | null {
    const rec = this.mods.get(moduleId);
    const panel = rec?.manifest.contributes.panel;
    return rec && panel ? packageText(rec.pkg, panel.file) : null;
  }

  /** API 3.0: HTML of a profile card of the module (the manifest's `view`, or a card it made), or null. */
  profileCardHtml(moduleId: string, card: string): string | null {
    return this.profileCardsOf(moduleId).find((c) => c.card === card)?.html ?? null;
  }

  /** API 4.10: the cards of a module — from its manifest and those it made — with the module's look. */
  profileCardsOf(moduleId: string): ModuleProfileCard[] {
    const rec = this.mods.get(moduleId);
    if (!rec) return [];
    const out: ModuleProfileCard[] = [];
    for (const c of rec.manifest.contributes.profileCards ?? []) {
      out.push({ card: c.id, title: c.title, html: packageText(rec.pkg, c.view) ?? '', size: c.size, look: rec.looks.get(c.id) ?? c.look, uses: c.uses ?? [], made: false });
    }
    for (const [id, c] of rec.cards) out.push({ card: id, title: c.title, html: c.html, size: c.size, look: rec.looks.get(id) ?? c.look, uses: c.uses, made: true });
    return out;
  }

  /** Whether a module may show the profile's counters in its cards (`profile.stats` granted). */
  hasProfileStats(moduleId: string): boolean {
    return !!this.mods.get(moduleId)?.host?.granted.has('profile.stats');
  }

  /** API 3.0: a message from a profile card (`{type:'opened'|'closed'}` when it is shown or goes). */
  sendProfileCardMessage(moduleId: string, card: string, data: unknown): void {
    if (!this.profileCardsOf(moduleId).some((c) => c.card === card)) return;
    this.mods.get(moduleId)?.host?.emit('profileCard.message', { card, data });
  }

  /** API 4.10: the person used an interactive element of a card of the module. */
  profileCardInput(moduleId: string, card: string, input: CardInput): void {
    if (!this.profileCardsOf(moduleId).some((c) => c.card === card)) return;
    this.mods.get(moduleId)?.host?.emit('profileCard.input', { card, input });
  }

  private async loadCards(rec: Rec): Promise<void> {
    rec.cards.clear();
    rec.looks.clear();
    const rows = await this.opts.storage.moduleData.list(rec.manifest.id, CARDS_SCOPE).catch(() => []);
    for (const r of rows) {
      if (r.key.startsWith('card:')) rec.cards.set(r.key.slice(5), r.value as MadeCard);
      else if (r.key.startsWith('look:')) rec.looks.set(r.key.slice(5), r.value as CardLook);
    }
  }

  private async putCardData(moduleId: string, key: string, value: unknown): Promise<void> {
    const json = JSON.stringify(value);
    await this.opts.storage.moduleData.put({ moduleId, scope: CARDS_SCOPE, key, value: JSON.parse(json), size: json.length, updatedAt: Date.now() });
  }

  /** A card of the module (manifest or made); otherwise E_NOT_FOUND. */
  private ownCard(moduleId: string, card: string): ModuleProfileCard {
    const c = this.profileCardsOf(moduleId).find((x) => x.card === card);
    if (!c) throw new RpcError(ErrorCodes.notFound, `profile card ${card} not found`);
    return c;
  }

  /** A card's HTML and look use only what it declared; counters of the profile need `profile.stats` in the manifest. */
  private checkCard(moduleId: string, uses: CardUse[], html: string, look?: CardLook | null): void {
    const missing = undeclaredUses(uses, html, look);
    if (missing.length) throw new RpcError(ErrorCodes.invalidParams, `the card uses ${missing.join(', ')} without declaring it`);
    if (uses.includes('live:profile') && !this.mods.get(moduleId)?.manifest.permissions.includes('profile.stats')) throw new RpcError(ErrorCodes.invalidParams, 'live:profile needs the profile.stats permission');
  }

  private async cardCall(moduleId: string, method: HostMethodName, params: unknown): Promise<unknown> {
    const rec = this.mods.get(moduleId)!;
    const panel = (card: string) => `${moduleId}:${card}`;
    switch (method) {
      case 'profileCards.create': {
        const p = params as HostMethodParams<'profileCards.create'>;
        if (rec.cards.size >= CARD_LIMITS.cards) throw new RpcError('E_QUOTA', `a module makes at most ${CARD_LIMITS.cards} cards`);
        this.checkCard(moduleId, p.uses, p.html, p.look);
        const id = 'u' + ulid().toLowerCase();
        const card: MadeCard = { title: p.title, html: p.html, uses: p.uses, ...(p.size ? { size: p.size } : {}), ...(p.look ? { look: p.look } : {}) };
        await this.putCardData(moduleId, 'card:' + id, card);
        rec.cards.set(id, card);
        this.changed();
        if (p.show) this.opts.profileCards?.show(panel(id));
        return { id };
      }
      case 'profileCards.update': {
        const p = params as HostMethodParams<'profileCards.update'>;
        const cur = rec.cards.get(p.id);
        if (!cur) throw new RpcError(ErrorCodes.notFound, `card ${p.id} was not made by ${moduleId}`);
        const next: MadeCard = { ...cur, ...(p.title !== undefined ? { title: p.title } : {}), ...(p.html !== undefined ? { html: p.html } : {}), ...(p.uses !== undefined ? { uses: p.uses } : {}) };
        this.checkCard(moduleId, next.uses, next.html, rec.looks.get(p.id) ?? next.look);
        await this.putCardData(moduleId, 'card:' + p.id, next);
        rec.cards.set(p.id, next);
        this.changed();
        return {};
      }
      case 'profileCards.remove': {
        const p = params as HostMethodParams<'profileCards.remove'>;
        if (!rec.cards.has(p.id)) throw new RpcError(ErrorCodes.notFound, `card ${p.id} was not made by ${moduleId}`);
        await this.opts.storage.moduleData.delete(moduleId, CARDS_SCOPE, 'card:' + p.id);
        await this.opts.storage.moduleData.delete(moduleId, CARDS_SCOPE, 'look:' + p.id);
        rec.cards.delete(p.id);
        rec.looks.delete(p.id);
        this.opts.profileCards?.forget(panel(p.id));
        this.changed();
        return {};
      }
      case 'profileCards.list':
        return { cards: [...rec.cards].map(([id, c]) => ({ id, title: c.title })) };
      case 'profileCards.setState': {
        const p = params as HostMethodParams<'profileCards.setState'>;
        this.ownCard(moduleId, p.card);
        const cur = this.opts.profileCards?.getState(panel(p.card)) ?? {};
        if (Object.keys({ ...cur, ...p.values }).length > CARD_LIMITS.stateKeys) throw new RpcError(ErrorCodes.invalidParams, `a card keeps at most ${CARD_LIMITS.stateKeys} values`);
        this.opts.profileCards?.setState(panel(p.card), p.values);
        return {};
      }
      case 'profileCards.getState': {
        const p = params as HostMethodParams<'profileCards.getState'>;
        this.ownCard(moduleId, p.card);
        return { values: this.opts.profileCards?.getState(panel(p.card)) ?? {} };
      }
      case 'profileCards.setLook': {
        const p = params as HostMethodParams<'profileCards.setLook'>;
        const c = this.ownCard(moduleId, p.card);
        if (p.look) this.checkCard(moduleId, c.uses, c.html, p.look);
        if (p.look) {
          await this.putCardData(moduleId, 'look:' + p.card, p.look);
          rec.looks.set(p.card, p.look);
        } else {
          await this.opts.storage.moduleData.delete(moduleId, CARDS_SCOPE, 'look:' + p.card);
          rec.looks.delete(p.card);
        }
        this.changed();
        return {};
      }
    }
    throw new RpcError(ErrorCodes.unknownMethod, method);
  }

  /** API 2.0: a message from the module's panel (`{type:'opened'|'closed'}` when the user opens/closes it). */
  sendPanelMessage(moduleId: string, data: unknown): void {
    this.mods.get(moduleId)?.host?.emit('panel.message', { data });
  }

  /** API 2.2: the gesture of a module tool; ids of the objects the module made. */
  async toolGesture(moduleId: string, toolId: string, gesture: { kind: 'click' | 'line' | 'path'; points: { x: number; y: number }[] }): Promise<Id[]> {
    const rec = this.mods.get(moduleId);
    const tool = rec?.manifest.contributes.tools?.find((x) => x.id === toolId && x.gesture);
    if (!rec?.host || !tool) throw new ModuleError('E_UNAVAILABLE', `no tool ${toolId}`);
    const res = await rec.host.call('toolGesture', { toolId, kind: gesture.kind, points: gesture.points.slice(0, 20000) });
    return (res.ids as Id[] | undefined) ?? [];
  }

  /** API 2.2: files the user picked for an import format of a module; ids of the objects it made. */
  async importFiles(moduleId: string, formatId: string, files: { name: string; type: string; size: number; data: Uint8Array }[]): Promise<Id[]> {
    const rec = this.mods.get(moduleId);
    const f = rec?.manifest.contributes.formats?.find((x) => x.id === formatId && x.kind === 'import');
    if (!rec?.host || !f) throw new ModuleError('E_UNAVAILABLE', `no import format ${formatId}`);
    if (!meets(rec.host.granted, 'canvas.write')) throw new ModuleError(ErrorCodes.permissionDenied, 'canvas.write is not granted');
    const res = await rec.host.call('importFile', { formatId, files: files as never });
    this.opts.logger.info('module.import', { moduleId, formatId, files: files.length });
    return (res.ids as Id[] | undefined) ?? [];
  }

  /** API 2.2: the selection as a file of an export format of a module. */
  async exportFile(moduleId: string, formatId: string, selection: Id[]): Promise<{ name: string; data: string | Uint8Array; type?: string }> {
    const rec = this.mods.get(moduleId);
    const f = rec?.manifest.contributes.formats?.find((x) => x.id === formatId && x.kind === 'export');
    if (!rec?.host || !f) throw new ModuleError('E_UNAVAILABLE', `no export format ${formatId}`);
    if (!meets(rec.host.granted, 'canvas.read')) throw new ModuleError(ErrorCodes.permissionDenied, 'canvas.read is not granted');
    // the selection as the module may read it (with `canvas.read.own` — its own objects only)
    const res = await rec.host.call('exportFile', { formatId, selection: this.readable(moduleId, selection) });
    this.opts.logger.info('module.export', { moduleId, formatId });
    return res as { name: string; data: string | Uint8Array; type?: string };
  }

  /** API 2.2: a click on a clickable area of an object; only for the owner's types that declared actions. */
  objectAction(objectId: Id, action: { id: string; data?: unknown }): void {
    const o = this.opts.docs.doc()?.get(objectId);
    const info = o ? this.typeInfo(o.type) : undefined;
    if (!o || !info?.contribution.capabilities?.actions) return;
    this.mods.get(info.moduleId)?.host?.emit('object.action', { objectId, action });
  }

  /**
   * API 4.6: the module that takes the keys while this object is the only one selected — its type has
   * `capabilities.keyboard`, the module is running and has `keyboard` granted — or null.
   */
  keyTarget(objectId: Id): string | null {
    const o = this.opts.docs.doc()?.get(objectId);
    const info = o ? this.typeInfo(o.type) : undefined;
    if (!info?.contribution.capabilities?.keyboard) return null;
    const host = this.mods.get(info.moduleId)?.host;
    return host && host.state === 'running' && host.granted.has('keyboard') ? info.moduleId : null;
  }

  /** API 4.6: a key for the selected object of a module (see keyTarget); false when it is not the module's to take. */
  sendKey(objectId: Id, key: { type: 'down' | 'up'; key: string; code: string; shift: boolean; alt: boolean; repeat: boolean }): boolean {
    const moduleId = this.keyTarget(objectId);
    if (!moduleId) return false;
    this.mods.get(moduleId)!.host!.emit('key', { objectId, ...key, key: key.key.slice(0, 40), code: key.code.slice(0, 40) });
    return true;
  }

  /** Whether the module may keep the keyboard in its live views and windows (API 4.6). */
  hasKeyboard(moduleId: string): boolean {
    return !!this.mods.get(moduleId)?.host?.granted.has('keyboard');
  }

  /** Messages from a module's sandboxed documents in the current second (API 4.6: at most SANDBOX_RATE). */
  private sandboxTimes = new Map<string, number[]>();

  /**
   * API 4.6: a message from one of the module's sandboxed documents (live view, panel, window, profile
   * card) is about to be passed on. More than SANDBOX_RATE a second, or one larger than the limit, is
   * dropped and counted as a violation.
   */
  acceptSandboxMessage(moduleId: string, size: number): boolean {
    if (size > SANDBOX_MESSAGE_MAX) {
      this.violation(moduleId, `sandbox message of ${size} bytes`, false);
      return false;
    }
    const now = Date.now();
    let times = this.sandboxTimes.get(moduleId);
    if (!times) this.sandboxTimes.set(moduleId, (times = []));
    while (times.length && now - times[0] > 1000) times.shift();
    if (times.length >= SANDBOX_RATE) {
      // one violation per second of overflow, not one per message
      if (times.length === SANDBOX_RATE) this.violation(moduleId, 'sandbox message rate exceeded', false);
      times.push(now);
      return false;
    }
    times.push(now);
    return true;
  }

  /** API 4.6: something a sandboxed document of the module was refused (its policy): a violation of the module. */
  sandboxViolation(moduleId: string, reason: string): void {
    this.violation(moduleId, `sandbox: ${reason.slice(0, 80)}`, false);
  }

  sendViewMessage(moduleId: string, objectId: Id, data: unknown): void {
    this.mods.get(moduleId)?.host?.emit('view.message', { objectId, data });
  }

  setLocale(locale: string): void {
    this.broadcast('locale.changed', { locale });
  }

  // ------------------------------------------------------------ rendering (§7.3)

  setZoom(zoom: number): void {
    const b = Math.pow(2, Math.round(Math.log2(Math.max(zoom, 1e-6))));
    if (b === this.zoomBucket) return;
    this.zoomBucket = b;
    // raster (mode B) objects re-render at the new scale
    const ids: Id[] = [];
    const doc = this.opts.docs.doc();
    if (doc) for (const [id] of this.render) {
      const o = doc.get(id);
      if (o && this.typeInfo(o.type)?.contribution.renderMode === 'B') ids.push(id);
    }
    if (ids.length) this.opts.onInvalidate?.(ids);
  }

  private clearModuleCaches(moduleId: string): void {
    for (const key of [...this.failedMigrations]) if (key.includes(moduleId)) this.failedMigrations.delete(key);
    this.invalidateModule(moduleId);
  }

  private invalidateModule(moduleId: string): void {
    const doc = this.opts.docs.doc();
    const ids: Id[] = [];
    for (const id of [...this.render.keys()]) {
      const o = doc?.get(id);
      if (!o || typeOwner(o.type) === moduleId) {
        this.render.delete(id);
        this.renderKeys.delete(id);
        if (o) ids.push(id);
      }
    }
    if (doc) for (const o of doc.allObjects()) if (typeOwner(o.type) === moduleId && !ids.includes(o.id)) ids.push(o.id);
    this.opts.onInvalidate?.(ids.length ? ids : null);
  }

  private renderKey(o: ObjectRecord, rev: number, mode: string): string {
    const c = this.renderKeys.get(o.id);
    if (c && c.rev === rev) return c.key;
    // geometry-independent: moving or rotating does not require a module round-trip
    const key = JSON.stringify([o.w, o.h, o.style, o.props, o.formatVersion, mode === 'B' ? this.zoomBucket : 0]);
    this.renderKeys.set(o.id, { rev, key });
    return key;
  }

  private schedule(moduleId: string, fn: () => Promise<void>): void {
    const run = () => {
      this.inflight.set(moduleId, (this.inflight.get(moduleId) ?? 0) + 1);
      void fn().finally(() => {
        this.inflight.set(moduleId, (this.inflight.get(moduleId) ?? 1) - 1);
        const next = this.queue.shift();
        if (next) next();
      });
    };
    if ((this.inflight.get(moduleId) ?? 0) < MAX_INFLIGHT_RENDERS) run();
    else this.queue.push(run);
  }

  /** ModuleRenderProvider for @vp/render. */
  readonly renderProvider = {
    primitives: (obj: ObjectRecord, rev: number): RenderResult => this.primitivesFor(obj, rev),
  };

  private primitivesFor(obj: ObjectRecord, rev: number): RenderResult {
    const info = this.typeInfo(obj.type);
    if (!info) return { status: 'unavailable', reason: 'module' };
    const rec = this.mods.get(info.moduleId)!;
    if (rec.host?.state !== 'running') return { status: 'unavailable', reason: rec.host?.state ?? 'stopped' };
    const ct = info.contribution;
    if (obj.formatVersion > ct.formatVersion) return { status: 'unavailable', reason: 'format' };
    if (obj.formatVersion < ct.formatVersion) {
      this.migrate(rec, obj, ct.formatVersion);
      const migKey = `${obj.id}:${obj.formatVersion}:${rec.manifest.id}@${rec.manifest.version}`;
      return this.failedMigrations.has(migKey) ? { status: 'unavailable', reason: 'migration' } : { status: 'pending', prims: this.render.get(obj.id)?.prims };
    }
    const mode = ct.renderMode;
    const key = this.renderKey(obj, rev, mode);
    let entry = this.render.get(obj.id);
    if (!entry) this.render.set(obj.id, (entry = { key: '' }));
    const stale = !!obj.props['$stale'];
    if (entry.key === key && entry.prims) return { status: 'ready', prims: entry.prims, stale };
    if (entry.failedKey === key) return { status: 'unavailable', reason: 'render' };
    if (entry.pendingKey !== key) {
      entry.pendingKey = key;
      const e = entry;
      const host = rec.host;
      this.schedule(rec.manifest.id, async () => {
        try {
          let prims: Primitive[];
          if (mode === 'B') {
            const scale = this.zoomBucket;
            const w = Math.max(1, Math.min(2048, Math.ceil(obj.w * scale)));
            const h = Math.max(1, Math.min(2048, Math.ceil(obj.h * scale)));
            const res = await host.call('renderRaster', { type: obj.type, object: obj, scale, width: w, height: h });
            prims = [{ k: 'raster', x: 0, y: 0, w: obj.w, h: obj.h, key: `${obj.id}:${key.length}:${hash(key)}`, bitmap: res.bitmap as ImageBitmap }];
          } else if (mode === 'C' && e.noRenderer) {
            prims = cardPrims(obj, info.manifest);
          } else {
            try {
              prims = (await host.call('render', { type: obj.type, object: obj, scale: 1 })).primitives as Primitive[];
            } catch (err) {
              if (mode === 'C' && err instanceof RpcError && err.code === ErrorCodes.notFound) {
                e.noRenderer = true;
                prims = cardPrims(obj, info.manifest);
              } else throw err;
            }
          }
          if (!info.contribution.capabilities?.actions && prims.some((p) => 'action' in p && p.action)) throw new ModuleError(ErrorCodes.invalidResult, 'primitives carry actions, but the type has no capabilities.actions');
          if (e.pendingKey !== key) return;
          e.key = key;
          e.prims = prims;
          this.opts.onPrimitives?.(prims);
          e.pendingKey = undefined;
          e.failedKey = undefined;
        } catch (err) {
          if (e.pendingKey !== key) return;
          e.pendingKey = undefined;
          e.failedKey = key;
          this.opts.logger.warn('module.render.failed', { moduleId: rec.manifest.id, type: obj.type, code: (err as { code?: string }).code ?? 'unknown' });
        }
        this.opts.onInvalidate?.([obj.id]);
      });
    }
    return { status: 'pending', prims: entry.prims };
  }

  /** Migrate an object to the module's current format; originals are kept until success (§8.5). */
  private migrate(rec: Rec, obj: ObjectRecord, to: number): void {
    const key = `${obj.id}:${obj.formatVersion}:${rec.manifest.id}@${rec.manifest.version}`;
    if (this.migrating.has(key) || this.failedMigrations.has(key)) return;
    this.migrating.add(key);
    const host = rec.host!;
    void host
      .call('migrate', { type: obj.type, object: obj, fromVersion: obj.formatVersion, toVersion: to })
      .then((res) => {
        const doc = this.opts.docs.doc();
        const migrated = ObjectRecordSchema.safeParse({ ...res.object, formatVersion: to });
        if (!migrated.success || migrated.data.id !== obj.id || migrated.data.type !== obj.type) throw new ModuleError(ErrorCodes.invalidResult, 'invalid migration result');
        if (!doc || doc.readOnly || !doc.has(obj.id)) return;
        const m = migrated.data as ObjectRecord;
        doc.applyOps(
          [{ t: 'update', table: 'objects', id: obj.id, before: { props: obj.props, style: obj.style, formatVersion: obj.formatVersion }, after: { props: m.props, style: m.style, formatVersion: to } }],
          { source: { kind: 'module', id: rec.manifest.id }, label: 'migration' },
          { history: true },
        );
        this.opts.logger.info('module.migrate', { moduleId: rec.manifest.id, type: obj.type, from: obj.formatVersion, to });
      })
      .catch((err) => {
        this.failedMigrations.add(key);
        this.opts.logger.warn('module.migrate.failed', { moduleId: rec.manifest.id, type: obj.type, code: (err as { code?: string }).code ?? 'unknown' });
        this.opts.onInvalidate?.([obj.id]);
      })
      .finally(() => this.migrating.delete(key));
  }

  // ------------------------------------------------------------ editor hooks

  async createDefaults(type: string, size: { w: number; h: number }): Promise<Record<string, unknown>> {
    const info = this.typeInfo(type);
    const host = info ? this.mods.get(info.moduleId)?.host : null;
    if (!info || !host || host.state !== 'running') throw new ModuleError('E_UNAVAILABLE', `module for ${type} is not available`);
    const res = await host.call('createDefaults', { type, size });
    return { ...(info.contribution.defaultProps ?? {}), ...res.props };
  }

  async view(type: string, kind: 'object' | 'panel', objectId: Id): Promise<string> {
    const info = this.typeInfo(type);
    const host = info ? this.mods.get(info.moduleId)?.host : null;
    if (!host || host.state !== 'running') throw new ModuleError('E_UNAVAILABLE', 'module is not available');
    return (await host.call('view', { type, kind, objectId })).html;
  }

  /**
   * Run a module command on the user's selection. API 4.0: for the time of the command the selection
   * is lent to the module within its canvas access (`own` reads and changes it as its own); a module
   * without any canvas access gets no selection.
   */
  async command(moduleId: string, commandId: string, selection: Id[]): Promise<void> {
    const host = this.mods.get(moduleId)?.host;
    if (!host || host.state !== 'running') throw new ModuleError('E_UNAVAILABLE', 'module is not available');
    if (!meets(host.granted, 'canvas.read') && !meets(host.granted, 'canvas.write')) selection = [];
    let lent = this.lent.get(moduleId);
    if (!lent) this.lent.set(moduleId, (lent = new Map()));
    for (const id of selection) {
      this.busy.set(id, (this.busy.get(id) ?? 0) + 1);
      lent.set(id, (lent.get(id) ?? 0) + 1);
    }
    try {
      await host.call('command', { commandId, selection });
    } finally {
      for (const id of selection) {
        this.busy.set(id, (this.busy.get(id) ?? 1) - 1);
        const n = (lent.get(id) ?? 1) - 1;
        if (n > 0) lent.set(id, n);
        else lent.delete(id);
      }
    }
  }

  /** API 4.0: objects lent to a module by a running command (id → number of commands). */
  private lent = new Map<string, Map<Id, number>>();

  /**
   * API 4.0: whether a module may read (or change) an object. `all` — any object; `own` — objects of
   * its types, core objects it created and the user has not edited since, and what a running command lent it.
   */
  private may(moduleId: string, kind: CanvasAccessKind, o: ObjectRecord): boolean {
    const host = this.mods.get(moduleId)?.host;
    const access = host ? canvasAccess(host.granted, kind) : 'none';
    if (access === 'all') return true;
    if (access === 'none') return false;
    return typeOwner(o.type) === moduleId || o.createdByModule === moduleId || !!this.lent.get(moduleId)?.has(o.id);
  }

  /** The object, if the module may change it. One it cannot see at all is reported missing. */
  private writable(moduleId: string, doc: CanvasDocument, id: Id): ObjectRecord {
    const o = doc.get(id);
    if (!o || (!this.may(moduleId, 'read', o) && !this.may(moduleId, 'write', o))) throw new RpcError(ErrorCodes.notFound, `object ${id} not found`);
    if (!this.may(moduleId, 'write', o)) throw new RpcError(ErrorCodes.permissionDenied, `object ${id} is not the module's own`);
    if (o.locked) throw new RpcError(ErrorCodes.forbidden, `object ${id} is locked`);
    return o;
  }

  /** The ids of existing objects the module may read. */
  private readable(moduleId: string, ids: Id[]): Id[] {
    const doc = this.opts.docs.doc();
    if (!doc) return [];
    return ids.filter((id) => {
      const o = doc.get(id);
      return !!o && this.may(moduleId, 'read', o);
    });
  }

  /** Whether the module may refer to the object (an end of an arrow or a link): it reads or changes it. */
  private reachable(moduleId: string, id: Id): boolean {
    const o = this.opts.docs.doc()?.get(id);
    return !!o && (this.may(moduleId, 'read', o) || this.may(moduleId, 'write', o));
  }

  isComputed(type: string): boolean {
    return !!this.typeInfo(type)?.contribution.capabilities?.computed;
  }

  /** Dependency recompute resolver (§8.3). */
  async compute(obj: ObjectRecord, inputs: ComputeInput[], signal: AbortSignal, timeoutMs?: number): Promise<ObjectPatch | null> {
    const info = this.typeInfo(obj.type);
    const host = info ? this.mods.get(info.moduleId)?.host : null;
    if (!host || host.state !== 'running') throw new ModuleError('E_UNAVAILABLE', 'module is not available');
    this.busy.set(obj.id, (this.busy.get(obj.id) ?? 0) + 1);
    try {
      return (await host.call('compute', { type: obj.type, object: obj, inputs }, { signal, timeoutMs })).patch;
    } finally {
      this.busy.set(obj.id, (this.busy.get(obj.id) ?? 1) - 1);
      this.computedStale.delete(obj.id);
    }
  }

  /** Conflict rule check (§9.4): blocks declared operations while the module works on the object. */
  conflict(obj: ObjectRecord, op: 'move' | 'resize' | 'rotate' | 'delete' | 'style'): string | null {
    const info = this.typeInfo(obj.type);
    if (!info || !(this.busy.get(obj.id) ?? 0)) return null;
    return info.contribution.conflicts?.includes(op) ? `E_CONFLICT:${info.moduleId}:${op}` : null;
  }

  // ------------------------------------------------------------ Platform API (module → host)

  private requireDoc(): CanvasDocument {
    const doc = this.opts.docs.doc();
    if (!doc) throw new RpcError(ErrorCodes.notFound, 'no open canvas');
    if (doc.readOnly) throw new RpcError('E_READ_ONLY', 'canvas is read-only');
    return doc;
  }

  /** Keep a picture as an attachment; `svg` (API 2.6) — an SVG one is drawn as PNG by the page first. */
  private async putImage(raw: Uint8Array, svg: boolean): Promise<{ hash: string; type: string; width: number | null; height: number | null }> {
    let data = raw;
    if (data.byteLength > 20 * 1024 * 1024) throw new RpcError(ErrorCodes.invalidParams, 'image is larger than 20 MB');
    if (svg && !imageType(data) && looksLikeSvg(data)) {
      const png = await this.opts.canvases?.svgToPng?.(data).catch(() => null);
      if (!png) throw new RpcError(ErrorCodes.invalidParams, 'the SVG picture cannot be drawn');
      data = png;
    }
    const type = imageType(data);
    if (!type) throw new RpcError(ErrorCodes.invalidParams, svg ? 'not a PNG, JPEG, GIF, WebP or SVG image' : 'not a PNG, JPEG, GIF or WebP image');
    const blob = new Blob([data as BlobPart], { type });
    const hash = await this.opts.storage.putAttachment(blob, type);
    const size = (await this.opts.imageSize?.(blob).catch(() => null)) ?? null;
    return { hash, type, width: size?.width ?? null, height: size?.height ?? null };
  }

  private write<T>(moduleId: string, label: string, fn: (tx: TxContext, doc: CanvasDocument) => T): T {
    const doc = this.requireDoc();
    try {
      return doc.transact((tx) => fn(tx, doc), { source: { kind: 'module', id: moduleId }, label }).result;
    } catch (e) {
      if (e instanceof RpcError) throw e;
      throw new RpcError((e as { code?: string }).code ?? ErrorCodes.internal, e instanceof Error ? e.message : String(e));
    }
  }

  private async handle(moduleId: string, method: HostMethodName, params: unknown): Promise<unknown> {
    switch (method) {
      case 'objects.get': {
        const doc = this.opts.docs.doc();
        const p = params as HostMethodParams<'objects.get'>;
        return { objects: doc ? p.ids.map((id) => doc.get(id)).filter((o): o is ObjectRecord => !!o && this.may(moduleId, 'read', o)) : [] };
      }
      case 'objects.query': {
        const doc = this.opts.docs.doc();
        const p = params as HostMethodParams<'objects.query'>;
        const out: ObjectRecord[] = [];
        if (doc) for (const o of doc.allObjects()) {
          if (!this.may(moduleId, 'read', o)) continue;
          if (p.types && !p.types.includes(o.type)) continue;
          if (p.text && !searchableText(o).includes(p.text.toLowerCase())) continue;
          out.push(o);
          if (out.length >= (p.limit ?? 1000)) break;
        }
        return { objects: out };
      }
      case 'objects.create': {
        const p = params as HostMethodParams<'objects.create'>;
        const ids = this.write(moduleId, 'module.create', (tx, doc) =>
          p.objects.map((o) => {
            const owner = typeOwner(o.type);
            const info = owner ? this.typeInfo(o.type) : null;
            if (owner && !info) throw new RpcError(ErrorCodes.forbidden, `unknown type ${o.type}`);
            // a module creates its own types (and core ones), never another module's objects
            if (owner && owner !== moduleId) throw new RpcError(ErrorCodes.forbidden, `type ${o.type} belongs to another module`);
            const rec = makeObject(doc.canvasId, {
              type: o.type,
              x: o.x,
              y: o.y,
              w: o.w,
              h: o.h,
              z: commands.nextZ(doc),
              props: { ...(info?.contribution.defaultProps ?? {}), ...(o.props ?? {}) },
              formatVersion: info?.contribution.formatVersion,
              ...(o.name !== undefined ? { name: o.name } : {}),
              ...(o.style || info?.contribution.defaultStyle || info?.contribution.text ? { style: { ...typeTextStyle(info), ...(info?.contribution.defaultStyle ?? {}), ...(o.style ? validStyle(o.style) : {}) } } : {}),
            });
            tx.create({ ...rec, createdByModule: moduleId });
            return rec.id;
          }),
        );
        return { ids };
      }
      case 'objects.update': {
        const p = params as HostMethodParams<'objects.update'>;
        this.write(moduleId, 'module.update', (tx, doc) => {
          for (const u of p.updates) {
            const o = this.writable(moduleId, doc, u.id);
            // the group's box follows its members: moving one inside a group the module may not change is refused
            const parent = o.parentId ? doc.get(o.parentId) : undefined;
            if (parent && !this.may(moduleId, 'write', parent) && GEOMETRY.some((k) => k in u.patch)) throw new RpcError(ErrorCodes.permissionDenied, `object ${u.id} is in a group that is not the module's own`);
            const patch: Record<string, unknown> = { ...u.patch };
            if (u.patch.props) patch['props'] = { ...o.props, ...u.patch.props };
            if (u.patch.style) patch['style'] = { ...o.style, ...u.patch.style };
            tx.update(u.id, patch as Partial<ObjectRecord>);
            if (o.parentId) commands.updateGroupBounds(tx, o.parentId);
          }
        });
        return {};
      }
      case 'objects.delete': {
        const p = params as HostMethodParams<'objects.delete'>;
        this.write(moduleId, 'module.delete', (tx, doc) => {
          for (const id of p.ids) if (doc.get(id)) this.writable(moduleId, doc, id);
          // the contents of groups and frames go too: with `canvas.write.own` all of it must be the module's
          for (const id of commands.withContents(doc, p.ids)) {
            if (!this.may(moduleId, 'write', doc.get(id)!)) throw new RpcError(ErrorCodes.permissionDenied, "a group or frame being deleted holds objects that are not the module's own");
          }
          commands.deleteDeep(tx, p.ids);
        });
        return {};
      }
      case 'links.get': {
        const doc = this.opts.docs.doc();
        const id = (params as HostMethodParams<'links.get'>).objectId;
        if (!doc || !this.readable(moduleId, [id]).length) return { links: [] };
        // a link is seen when both its ends are
        return { links: doc.linksOf(id).filter((l) => this.readable(moduleId, [l.from, l.to]).length === 2) };
      }
      case 'links.create': {
        const p = params as HostMethodParams<'links.create'>;
        const lt = this.linkType(p.type);
        if (!lt) throw new RpcError(ErrorCodes.forbidden, `unknown link type ${p.type}`);
        if (lt.moduleId !== moduleId) throw new RpcError(ErrorCodes.forbidden, `link type ${p.type} belongs to another module`);
        const id = this.write(moduleId, 'module.link', (tx, doc) => {
          const from = doc.get(p.from);
          const to = doc.get(p.to);
          if (!from || !to || !this.reachable(moduleId, p.from) || !this.reachable(moduleId, p.to)) throw new RpcError(ErrorCodes.notFound, 'link endpoint not found');
          if (lt.from?.length && !lt.from.includes(from.type)) throw new RpcError(ErrorCodes.forbidden, 'source type not allowed');
          if (lt.to?.length && !lt.to.includes(to.type)) throw new RpcError(ErrorCodes.forbidden, 'target type not allowed');
          const link: LinkRecord = { id: ulid(), canvasId: doc.canvasId, type: p.type, from: p.from, to: p.to, directed: lt.directed, formatVersion: 1, props: p.props ?? {} };
          tx.createLink(link);
          return link.id;
        });
        return { id };
      }
      case 'links.delete': {
        const p = params as HostMethodParams<'links.delete'>;
        const all = canvasAccess(this.mods.get(moduleId)?.host?.granted ?? [], 'write') === 'all';
        this.write(moduleId, 'module.unlink', (tx, doc) => {
          for (const id of p.ids) {
            const l = doc.getLink(id);
            if (!l) continue;
            // with `canvas.write.own` — only links of the module's own link types
            if (!all && this.linkType(l.type)?.moduleId !== moduleId) throw new RpcError(ErrorCodes.permissionDenied, `link ${id} is not the module's own`);
            tx.deleteLink(id);
          }
        });
        return {};
      }
      case 'ui.notify': {
        const p = params as HostMethodParams<'ui.notify'>;
        const name = this.mods.get(moduleId)?.manifest.name;
        const label = typeof name === 'string' ? name : name?.[this.opts.locale()] ?? moduleId;
        const commands = this.mods.get(moduleId)?.manifest.contributes.commands ?? [];
        const bad = p.actions?.find((a) => !commands.some((c) => c.id === a.command));
        if (bad) throw new RpcError(ErrorCodes.invalidParams, `unknown command ${bad.command}`);
        const actions = p.actions?.map((a) => ({ label: a.label, run: () => void this.command(moduleId, a.command, this.opts.docs.selection()).catch(() => undefined) }));
        this.opts.docs.notify(p.level, `${label}: ${p.message}`, p.objectId, actions);
        return {};
      }
      case 'fonts.list': {
        const own = [...new Set(this.mods.get(moduleId)?.fonts.map((f) => f.family) ?? [])].map((family) => ({ family, css: `"${family}", system-ui, sans-serif`, source: 'module' as const }));
        return { fonts: [...(this.opts.fonts?.platform() ?? []).map((f) => ({ ...f, source: 'platform' as const })), ...own] };
      }
      case 'fonts.faces': {
        const p = params as HostMethodParams<'fonts.faces'>;
        const want = new Set(p.families);
        const own = (this.mods.get(moduleId)?.fonts ?? []).filter((f) => want.has(f.family));
        const platformNames = (this.opts.fonts?.platform() ?? []).map((f) => f.family).filter((f) => want.has(f));
        const platform = platformNames.length && this.opts.fonts ? await this.opts.fonts.faces(platformNames) : [];
        return { faces: [...platform, ...own] };
      }
      case 'diagrams.validate': {
        const p = params as HostMethodParams<'diagrams.validate'>;
        const { diagnostics } = parseDiagram(p.source, { shapes: this.diagramShapes() });
        return { diagnostics: diagnostics.map((d) => ({ line: d.line, col: d.col, end: d.end, severity: d.severity, code: d.code, message: formatDiagnostic(d, this.opts.locale()), ...(d.fix ? { fix: d.fix } : {}) })) };
      }
      case 'diagrams.create': {
        const p = params as HostMethodParams<'diagrams.create'>;
        const { model } = parseDiagram(p.source, { shapes: this.diagramShapes() });
        if (!model) throw new RpcError(ErrorCodes.invalidParams, 'not a DSkhema source');
        const size = this.diagramSize(p.source);
        const id = this.write(moduleId, 'module.diagram', (tx, doc) => {
          const rec = makeObject(doc.canvasId, { type: CoreTypes.diagram, x: p.x ?? 0, y: p.y ?? 0, w: size.w, h: size.h, z: commands.nextZ(doc), props: { source: p.source, lang: 'dskhema', langVersion: LANG_VERSION } });
          tx.create({ ...rec, createdByModule: moduleId });
          return rec.id;
        });
        return { id };
      }
      case 'diagrams.update': {
        const p = params as HostMethodParams<'diagrams.update'>;
        this.write(moduleId, 'module.diagram', (tx, doc) => {
          if (doc.get(p.id)?.type !== CoreTypes.diagram) throw new RpcError(ErrorCodes.notFound, `scheme ${p.id} not found`);
          const o = this.writable(moduleId, doc, p.id);
          const before = this.diagramSize(String(o.props['source'] ?? ''));
          const after = this.diagramSize(p.source);
          const k = Math.min(o.w / before.w, o.h / before.h) || 1;
          tx.update(o.id, { w: after.w * k, h: after.h * k, props: { ...o.props, source: p.source, lang: 'dskhema', langVersion: LANG_VERSION } });
        });
        return {};
      }
      case 'log': {
        const p = params as HostMethodParams<'log'>;
        this.opts.logger.log(p.level, `mod:${moduleId}:${p.code}`, p.data);
        return {};
      }
      // ---- API 1.6
      case 'settings.get':
        return { values: this.settingsOf(moduleId) };
      case 'canvas.info': {
        const doc = this.opts.docs.doc();
        if (!doc) return { id: null, name: '', objects: 0, bounds: null };
        let n = 0;
        let x0 = Infinity;
        let y0 = Infinity;
        let x1 = -Infinity;
        let y1 = -Infinity;
        // with `canvas.read.own` — the count and bounds of the module's own objects
        for (const o of doc.allObjects()) {
          if (!this.may(moduleId, 'read', o)) continue;
          n++;
          if (o.parentId) continue;
          x0 = Math.min(x0, o.x);
          y0 = Math.min(y0, o.y);
          x1 = Math.max(x1, o.x + o.w);
          y1 = Math.max(y1, o.y + o.h);
        }
        return { id: doc.canvasId, name: this.opts.docs.canvasName?.() ?? '', objects: n, bounds: n ? { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } : null };
      }
      case 'selection.get':
        return { ids: this.readable(moduleId, this.opts.docs.selection()) };
      case 'selection.set': {
        const ids = this.readable(moduleId, (params as HostMethodParams<'selection.set'>).ids);
        this.opts.docs.setSelection?.(ids);
        return {};
      }
      case 'viewport.focus': {
        const ids = this.readable(moduleId, (params as HostMethodParams<'viewport.focus'>).ids);
        if (ids.length) this.opts.docs.focus?.(ids);
        return {};
      }
      case 'connectors.create': {
        const p = params as HostMethodParams<'connectors.create'>;
        if (!this.reachable(moduleId, p.from) || !this.reachable(moduleId, p.to)) throw new RpcError(ErrorCodes.notFound, 'both ends must exist');
        if (!this.opts.docs.connect) throw new RpcError(ErrorCodes.unknownMethod, 'arrows cannot be drawn here');
        return { id: this.opts.docs.connect(p.from, p.to, { label: p.label, arrow: p.arrow, module: moduleId }) };
      }
      case 'windows.open':
        return this.openWindow(moduleId, params as HostMethodParams<'windows.open'>);
      case 'windows.close': {
        const p = params as HostMethodParams<'windows.close'>;
        this.ownWindow(moduleId, p.handle);
        this.opts.windows?.close(p.handle);
        this.windowClosed(p.handle);
        return {};
      }
      case 'windows.setTitle': {
        const p = params as HostMethodParams<'windows.setTitle'>;
        this.ownWindow(moduleId, p.handle);
        this.opts.windows?.setTitle(p.handle, p.title);
        return {};
      }
      case 'files.open': {
        if (!this.opts.device) throw new RpcError(ErrorCodes.unknownMethod, 'files are not available here');
        return { files: await this.opts.device.openFiles(moduleId, params as HostMethodParams<'files.open'>) };
      }
      case 'files.save': {
        if (!this.opts.device) throw new RpcError(ErrorCodes.unknownMethod, 'files are not available here');
        const p = params as HostMethodParams<'files.save'>;
        if ((typeof p.data === 'string' ? p.data.length : p.data.byteLength) > 50_000_000) throw new RpcError(ErrorCodes.invalidParams, 'file is too large');
        return { saved: await this.opts.device.saveFile(moduleId, p.name, p.data, p.type) };
      }
      case 'clipboard.readText': {
        if (!this.opts.device) throw new RpcError(ErrorCodes.unknownMethod, 'clipboard is not available here');
        return { text: await this.opts.device.readClipboard(moduleId) };
      }
      case 'clipboard.writeText': {
        if (!this.opts.device) throw new RpcError(ErrorCodes.unknownMethod, 'clipboard is not available here');
        await this.opts.device.writeClipboard(moduleId, (params as HostMethodParams<'clipboard.writeText'>).text);
        return {};
      }
      case 'attachments.put':
        return this.putImage((params as HostMethodParams<'attachments.put'>).data, false);
      case 'canvases.create': {
        const p = params as HostMethodParams<'canvases.create'>;
        if (!this.opts.canvases) throw new RpcError(ErrorCodes.unknownMethod, 'canvases cannot be made here');
        const objects = p.objects.map((o) => {
          const owner = typeOwner(o.type);
          const info = owner ? this.typeInfo(o.type) : null;
          if (owner && (!info || owner !== moduleId)) throw new RpcError(ErrorCodes.forbidden, `type ${o.type} cannot be created by ${moduleId}`);
          const style = { ...typeTextStyle(info), ...(info?.contribution.defaultStyle ?? {}), ...(o.style ? validStyle(o.style) : {}) };
          return {
            ...o,
            props: { ...(info?.contribution.defaultProps ?? {}), ...(o.props ?? {}) },
            style: Object.keys(style).length ? style : undefined,
            formatVersion: info?.contribution.formatVersion,
          };
        });
        const hashes = new Map<string, string>();
        for (const img of p.images ?? []) {
          if (hashes.has(img.ref)) throw new RpcError(ErrorCodes.invalidParams, `image ${img.ref} is given twice`);
          hashes.set(img.ref, (await this.putImage(img.data, true)).hash);
        }
        const id = ulid();
        const records = buildCanvas(id, objects, hashes);
        await this.opts.canvases.create({ id, name: p.name }, records);
        let made = this.madeCanvases.get(moduleId);
        if (!made) this.madeCanvases.set(moduleId, (made = new Set()));
        made.add(id);
        this.opts.logger.info('module.canvas.create', { moduleId, canvasId: id, objects: records.length, images: hashes.size });
        return { id, objects: records.length };
      }
      case 'canvases.open': {
        const p = params as HostMethodParams<'canvases.open'>;
        if (!this.madeCanvases.get(moduleId)?.has(p.id)) throw new RpcError(ErrorCodes.notFound, `canvas ${p.id} was not made by ${moduleId}`);
        if (!this.opts.canvases?.open(p.id)) throw new RpcError(ErrorCodes.forbidden, 'a canvas opens only in answer to a user action');
        return {};
      }
      case 'objects.createTree': {
        const p = params as HostMethodParams<'objects.createTree'>;
        if (!this.opts.docs.insertTree) throw new RpcError(ErrorCodes.unknownMethod, 'objects cannot be created here');
        if (this.opts.docs.readOnly?.()) throw new RpcError('E_READ_ONLY', 'the canvas is read-only here');
        const { objects, connectors, refs } = this.prepareTree(moduleId, p, true);
        // with `canvas.write.own` new frames do not take objects already on the canvas
        const ownOnly = canvasAccess(this.mods.get(moduleId)?.host?.granted ?? [], 'write') !== 'all';
        const ids = this.opts.docs.insertTree(objects, connectors, undefined, moduleId, ownOnly);
        const out: Record<string, Id> = {};
        for (const [ref, i] of refs) out[ref] = ids[i];
        return { ids, refs: out };
      }
      case 'profileCards.create':
      case 'profileCards.update':
      case 'profileCards.remove':
      case 'profileCards.list':
      case 'profileCards.setState':
      case 'profileCards.getState':
      case 'profileCards.setLook':
        return this.cardCall(moduleId, method, params);
      case 'storage.get':
      case 'storage.set':
      case 'storage.delete':
      case 'storage.list':
        return this.storageCall(moduleId, method, params as { scope: 'canvas' | 'app'; key?: string; value?: unknown; prefix?: string });
      case 'modules.tools':
        return { tools: this.mcpTools(moduleId) };
      case 'modules.call': {
        const p = params as HostMethodParams<'modules.call'>;
        const target = this.mods.get(p.moduleId);
        if (!target || p.moduleId === moduleId) throw new RpcError(ErrorCodes.notFound, `module ${p.moduleId} not found`);
        // the target must have declared the tool: a module is only called the way it agreed to be
        if (!(target.manifest.contributes.mcpTools ?? []).some((t) => t.name === p.name)) throw new RpcError(ErrorCodes.notFound, `module ${p.moduleId} has no tool ${p.name}`);
        if (!target.host || target.host.state !== 'running') throw new RpcError(ErrorCodes.moduleError, `module ${p.moduleId} is not running`);
        this.opts.logger.info('module.mcp.call', { caller: moduleId, moduleId: p.moduleId, tool: p.name });
        const res = await target.host.call('mcpTool', { name: p.name, args: p.args ?? {}, caller: moduleId });
        let size = 0;
        try {
          size = JSON.stringify(res.result ?? null).length;
        } catch {
          throw new RpcError(ErrorCodes.moduleError, 'tool result is not JSON');
        }
        if (size > 1_000_000) throw new RpcError(ErrorCodes.moduleError, 'tool result is too large');
        return { result: res.result ?? null };
      }
    }
    throw new RpcError(ErrorCodes.unknownMethod, method);
  }
}

/** Patch keys that move or resize an object (and so its group's box). */
const GEOMETRY = ['x', 'y', 'w', 'h', 'rotation'];

/** A style from a module: only valid fields (StyleSchema), unknown ones dropped. */
function validStyle(raw: Record<string, unknown>): Record<string, unknown> {
  const r = StyleSchema.partial().safeParse(raw);
  if (!r.success) throw new RpcError(ErrorCodes.invalidParams, 'invalid style: ' + r.error.issues.map((i) => i.path.join('.') + ' ' + i.message).join('; '));
  return r.data as Record<string, unknown>;
}

function hash(s: string): string {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return (h >>> 0).toString(36);
}

/** Static card for mode C objects when no live view is active and no preview renderer exists. */
function cardPrims(obj: ObjectRecord, m: ModuleManifest): Primitive[] {
  const name = typeof m.name === 'string' ? m.name : Object.values(m.name)[0] ?? m.id;
  return [
    { k: 'shape', shape: 'rect', x: 0, y: 0, w: obj.w, h: obj.h, radius: 6, fill: { kind: 'solid', color: '#ffffff' }, stroke: { color: '#cbd5e1', width: 1, dash: 'solid' } },
    {
      k: 'text',
      x: 0,
      y: 0,
      w: obj.w,
      h: obj.h,
      text: name,
      style: { fontSize: Math.max(8, Math.min(16, obj.h / 5)), color: '#64748b', align: 'center', valign: 'middle' },
    },
  ];
}

export { PackageError, compareSemver };

